import { MathLerp } from "../core/utils";
import { scDeclareSceneUniformBuffer, scGetBasePassComputeAO, scGetBasePassInterpolatorDecl, scGetBasePassLightShading, scGetBasePassMaterialParamsLoad, scGetBasePassUniformTexDeclPS, scGetBasePassUniformVarDeclPS } from "./shaderBasePass";
import { scGetCommonFuncsDeclarations, scGetGIFuncsDeclarations, scGetSphericalFuncsDeclarations, scPBRDeclarations } from "./shaderCommon";
import { scGetSDFFuncsDeclarations, scGetSDFShadowDeclarations } from "./shaderSDF";


export function GetShaderSourceApplyFireVS() {
    return (
        /* glsl */ `#version 300 es

	precision highp float;

	layout(location = 0) in vec2 VertexBuffer;
	layout(location = 1) in vec2 TexCoordsBuffer;

	uniform float ViewportRatio;

	uniform vec3 PosPlaneSpace;

	out vec2 vsOutTexCoords;

	void main()
	{
		vsOutTexCoords = TexCoordsBuffer;

		vec2 pos = VertexBuffer.xy;

		vec2 scale = vec2(0.1 * 0.2);
		scale.x /= ViewportRatio;
		
		//calculate offset
		vec2 posOffset = PosPlaneSpace.xy;
		
		pos = (pos.xy * scale.xy) + posOffset.xy;

		pos.y *= -1.0;

		gl_Position = vec4(pos.xy, 0.0, 1.0);
	}`
    );
}

export function GetShaderSourceApplyFirePS() {
    return (
        /* glsl */ `#version 300 es

		precision highp float;
		precision highp sampler2D;
	
		layout(location = 0) out float OutFire;

		` +scDeclareSceneUniformBuffer()+/* glsl */`
	
		uniform sampler2D NoiseTexture;
		uniform int EPaintingState;
	
		in vec2 vsOutTexCoords;

		`+scGetCommonFuncsDeclarations()+ /* glsl */`

	void main()
	{
		float d = 1.25 * length(vsOutTexCoords - vec2(0.5));
		d = min(1.0, d);
		d = 1.f - d;

		float thres = 0.5;
		if(EPaintingState > 2)
		{
			thres = 0.75;
		}
		float s = 1.0;

		if(d < thres)
		{
			float sThres = thres - 0.02;
			if(EPaintingState > 2)
			{
				sThres = 0.6;
			}
			if(d > sThres)
			{
				s = MapToRange(d, sThres,thres, 0.0, 1.0);
				//s = 1.0;
			}
			else
			{
				s = 0.0;
			}
		}
		else
		{
			s = 1.0;
		}

		float fireScale = 3.0;
		if(EPaintingState > 2)
		{
			fireScale = 2.0;
		}

		OutFire = s * s * s * fireScale;

	}`
    );
}

export function GetShaderSourcePropagationPS() {
    return /* glsl */ `#version 300 es
	
	precision highp float;
	precision highp sampler2D;

	layout(location = 0) out float OutFire;

	` +scDeclareSceneUniformBuffer()+/* glsl */`

	uniform highp sampler2D FireTexture;
	uniform highp sampler2D NoiseTexture;

	uniform float NoiseTextureInterpolator;
	uniform int EPaintingState;

	in vec2 vsOutTexCoords;

	`+scGetCommonFuncsDeclarations()+ /* glsl */`

	void main()
	{
		
		ivec2 SampleCoord = ivec2(gl_FragCoord.xy);

		float curFire = texelFetch(FireTexture, SampleCoord, 0).r;

		vec2 dirToCenter = 2.0 * (vsOutTexCoords - vec2(0.5, 0.4));
		float distToCenter = length(dirToCenter);

	#if 0
		const float kMutualScale = 1.0 + float(` + Math.random() * 1.5 + /* glsl */ `);
		const float GFireSpreadSpeed = 3. * kMutualScale;
		const float NoiseAdvectedSpreadStrength = 0.45f;
		const float GFuelConsumeSpeed = float(` + MathLerp(0.4, 0.9, Math.random()) + /* glsl */ `) * kMutualScale;
		const float GFireDissipationSpeed = 0.05f * kMutualScale; //How fast fire fades when no more fuel is left. Try 0.05
		const float kIgnitionThreshold = 0.75; //When pixel becomes a source of fire
		const float kHeatRaiseSpeedDuringCombustion = 1.0;
		const float kMaxHeat = 30.0;
	#else
		const float kMutualScale = 1.0;
		const float GFireSpreadSpeed = 7.0;//3.5;
		const float NoiseAdvectedSpreadStrength = 0.45f;
		const float GFuelConsumeSpeed = 4.0 * kMutualScale;
		const float GFireDissipationSpeed = 0.05f * kMutualScale; //How fast fire fades when no more fuel is left. Try 0.05
		const float kIgnitionThreshold = 0.75; //When pixel becomes a source of fire
		const float kHeatRaiseSpeedDuringCombustion = 1.0;
		const float kMaxHeat = 3.0;
	#endif

			{
				const vec3 kRandomValues = vec3(float(` +
			Math.random() +
			/* glsl */ `), float(` +
			Math.random() +
			/* glsl */ `),
				float(` +
			Math.random() +
			/* glsl */ `)
				);
				
				float originalFireValue = curFire;
			
				//read neighbor fire values
				float neighborFire;
	
				float accumulatedFire = 0.f;

				if(EPaintingState == 1 || EPaintingState == 2)
				{
					//right
					neighborFire = texelFetch(FireTexture, SampleCoord + ivec2(1, 0), 0).r;
					{
						accumulatedFire += neighborFire;
					}
					if(SampleCoord.x > 0)
					{
						//left
						neighborFire = texelFetch(FireTexture, SampleCoord - ivec2(1, 0), 0).r;
						{
							accumulatedFire += neighborFire ;
						}
					}	
					if(SampleCoord.y > 0)
					{
						//up
						neighborFire = texelFetch(FireTexture, SampleCoord - ivec2(0, 1), 0).r;
						{
							accumulatedFire += neighborFire ;
						}
					}	
					//down
					neighborFire = texelFetch(FireTexture, SampleCoord + ivec2(0, 1), 0).r;
					{
						accumulatedFire += neighborFire;
					}

					accumulatedFire *= 0.3;
				
					curFire -= clamp(3.0 - accumulatedFire, 0.0, 3.0) * SceneBuffer.DeltaTime * 2.0;

					curFire = max(0.0, curFire);
				}
				else if(EPaintingState > 3)
				{
			#if 1//NOISE UV SPREAD
			{
				vec2 noisePrecisionMinMax = vec2(0.01, 0.2);
				float noisePrecision = mix(noisePrecisionMinMax.x, noisePrecisionMinMax.y, kRandomValues.x) * 3.0;
				vec2 offsetLengthMinMax = vec2(0.01, 0.1); //>= 0.2 for cool effects
				float offsetLength = mix(offsetLengthMinMax.x, offsetLengthMinMax.y, kRandomValues.y);

				//map to UV
				const float fireTextureSize = 512.0;
				vec2 texCoords = vec2((float(SampleCoord.x) + 0.5) / (fireTextureSize), (float(SampleCoord.y) + 0.5) / (fireTextureSize * 1.2));
				vec2 noiseSamplingUV = texCoords;
				noiseSamplingUV *= noisePrecision;
				const float noiseChangeSpeed = float(`+(Math.random() * 10.0)+/* glsl */`);
				noiseSamplingUV += vec2(SceneBuffer.Time * 0.00013, SceneBuffer.Time * 0.000093) * noiseChangeSpeed;
				vec3 noiseVec3 = textureLod(NoiseTexture, noiseSamplingUV, 0.f).rgb;
				vec2 noiseDirVec = noiseVec3.rg;
				{
					float t = mod(SceneBuffer.Time, 8.0) * 0.25;
					if(t > 1.0)
					{
						noiseDirVec.x = mix(noiseVec3.z, noiseVec3.x, 2.0 - t);
					}
					else
					{
						//t < 1
						noiseDirVec.x = mix(noiseVec3.x, noiseVec3.z, t);
					}
				}
				noiseDirVec.x = clamp(MapToRange(noiseDirVec.x, 0.4, 0.6, -1.0, 1.0), -1.0, 1.0);
				noiseDirVec.y = clamp(MapToRange(noiseDirVec.y, 0.4, 0.6, -1.0, 1.0), -1.0, 1.0);
	
				float distWeight = 1.f - min(1.0, length(noiseDirVec));
				distWeight *= distWeight;
	
				noiseDirVec *= offsetLength;

	
				neighborFire = textureLod(FireTexture, texCoords + noiseDirVec, 0.f).r;
				if(neighborFire > originalFireValue)
				{
					accumulatedFire += neighborFire * distWeight * NoiseAdvectedSpreadStrength * GFireSpreadSpeed * SceneBuffer.DeltaTime;
				}
	
			}
			#endif

			#if 1
			{
				vec2 noiseDirVec = -normalize(dirToCenter) * 0.005;
				neighborFire = textureLod(FireTexture, vsOutTexCoords + noiseDirVec, 0.f).r;
				if(neighborFire > originalFireValue )
				{
					accumulatedFire += neighborFire * GFireSpreadSpeed * SceneBuffer.DeltaTime * 0.25;
				}
			}
			#endif
	
				vec3 noiseTexture = texelFetch(NoiseTexture, SampleCoord, 0).rgb;
	
			#if 1//NOISE
				float finalNoise;
	
				if(NoiseTextureInterpolator < 1.f)
				{
					finalNoise = mix(noiseTexture.x, noiseTexture.y, NoiseTextureInterpolator);
				}
				else if(NoiseTextureInterpolator < 2.f)
				{
					finalNoise = mix(noiseTexture.y, noiseTexture.z, fract(NoiseTextureInterpolator));
				}
				else
				{
					finalNoise = mix(noiseTexture.z, noiseTexture.x, fract(NoiseTextureInterpolator));
				}
				accumulatedFire *= max(finalNoise, 0.0f);
			#endif

				curFire += accumulatedFire;
				float minF = 2.9;
				if(EPaintingState == 6)
				{
					minF = 3.0;
				}
				curFire = min(minF, curFire);

			}//paint state
	
	
			}
	
			OutFire = curFire;
		

	}`;
}


export function GetShaderSourceBasePassDestrPlanePS(sceneSDFFunc : string) {
    return /* glsl */ `#version 300 es
		
		precision highp float;
		precision highp sampler2D;
		precision highp samplerCube;
	
		layout(location = 0) out vec3 OutColor;
	
		` +scDeclareSceneUniformBuffer()+/* glsl */`

		` +scGetBasePassUniformVarDeclPS()+/* glsl */`

		` +scGetBasePassUniformTexDeclPS()+/* glsl */`

		uniform sampler2D FireTexture;
		uniform float PaintBrightnessScale;

		`+scGetBasePassInterpolatorDecl(true)+ /* glsl */`

		#define BASEPASS_SHADOW
		
		`+scGetCommonFuncsDeclarations()+ /* glsl */`
		`+scGetSphericalFuncsDeclarations()+ /* glsl */`
		`+scPBRDeclarations(true)+ /* glsl */`
		`+scGetGIFuncsDeclarations()+ /* glsl */`
		`+scGetSDFFuncsDeclarations(sceneSDFFunc)+ /* glsl */`
		`+scGetSDFShadowDeclarations()+ /* glsl */`

		void main()
		{
			vec3 light = vec3(0.0);
			PBRSettings s;

			`+scGetBasePassMaterialParamsLoad()+/* glsl */`

			float curFire = texture(FireTexture, interpolatorTexCoords.xy).r;

			if(curFire > 2.9)
			{
				discard;
			}

			s.PixelPos = interpolatorWorldSpacePos;
			s.vToCam = normalize(SceneBuffer.CameraPosition.xyz - interpolatorWorldSpacePos);
			float AO = 1.0;
			`+scGetBasePassComputeAO()+/* glsl */`

			vec3 albedoOr = s.Albedo;

			s.Albedo *= 2.5;

			`+scGetBasePassLightShading()+ /* glsl */`

			//vec3 fireColor = vec3(1.0, 0.5, 0.1);
			vec3 fireColor = vec3(0.2, 0.9, 0.1) * 0.75;
			//vec3 fireColor = s.Albedo;

			light = mix(albedoOr * 0.5, light, 0.5);

			{
				float scale = 1.0;
				#if 1 //EDGE FADE-OUT
					const float edgeShadow = 0.75;
					const highp float rectPow = 16.f;
					vec2 ndcPos = MapToRange(interpolatorTexCoords, 0.0, 1.0, -1.0, 1.0);
					highp float rectCircleLength = pow(abs(ndcPos.x), rectPow) + pow(abs(ndcPos.y), rectPow);
					const float thres = 0.1;
					if(rectCircleLength < thres)
					{
						scale = 1.0;
					}
					else
					{
						scale = MapToRange(rectCircleLength, thres, 2.0, 1.0, edgeShadow);
						//scale = smoothstep(0.0, 1.0, scale);
					}
					const float edgeThres = 0.99;

					if(abs(ndcPos.x) > edgeThres)
					{
						scale *= MapToRange(abs(ndcPos.x), edgeThres, 1.0, 1.0, edgeShadow);
					}
					if(abs(ndcPos.y) > edgeThres)
					{
						scale *= MapToRange(abs(ndcPos.y), edgeThres, 1.0, 1.0, edgeShadow);
					}
					scale *= scale;
					scale = min(1.0, scale + 0.05);
					light *= scale;
				#endif
			}

			OutColor = light * 0.9 * PaintBrightnessScale + fireColor * clamp(curFire * 0.75, 0.0, 5.0);
			//OutColor = s.Albedo * 0.5 + fireColor * clamp(curFire, 0.0, 5.0);

		}`;
}
